import partnerDataCurrentMutation from '@/mixins/currentMutation/partnerDataCurrentMutation';
import dashboardBoxes from '@/mixins/dashboardBoxes';
import phoneLink from '@/mixins/phoneLink';
import { getQuestionResponseHours } from '@/feature/questions/helpers/questionPricingHelpers';

export default {
  mixins: [partnerDataCurrentMutation, dashboardBoxes, phoneLink],
  computed: {
    questionService() {
      return {
        icon: 'chat',
        title: this.$t('dashboard.services.question.title'),
        dataCy: 'clientZoneDashboardServicesQuestions',
        subtitle: `${this.$t('dashboard.services.question.subtitle', {
          hours: getQuestionResponseHours({
            locale: this.$i18n.locale,
            isB2bActive: !!(this.$auth.user && this.$auth.user.benefit.active)
          })
        })}${
          this.$i18n.locale !== 'en' && this.$auth.user && !this.$auth.user.benefit.active
            ? ` ${this.$t('dashboard.services.question.b2cSufix', {
                priceCzk: this.prices.czk,
                priceEur: this.prices.eur
              })}`
            : ''
        }`,
        text:
          this.$auth.user.services.questionMessage &&
          this.$auth.user.services.question === 'visible'
            ? this.hintText(this.$auth.user.services.questionMessage)
            : this.$t('dashboard.services.question.text'),
        buttons: [
          {
            type: 'block',
            disabled: this.$auth.user.services.question === 'visible',
            text: this.$t('dashboard.services.question.button'),
            clickAction: () => {
              this.gtmPush({
                event: 'button_click',
                'button.type': this.$t('dashboard.services.question.button')
              });
              this.$router.push(this.$localePath('questions'));
            }
          }
        ]
      };
    },
    labOnlineService() {
      return {
        icon: 'report',
        title: this.$t('dashboard.services.labTest.title'),
        dataCy: 'clientZoneDashboardServicesLabTest',
        subtitle: this.$t('dashboard.services.labTest.subtitle'),
        text: this.$t('dashboard.services.labTest.text'),
        buttons: [
          {
            type: 'block',
            disabled: this.$auth.user.services.question === 'visible',
            text: this.$t('dashboard.services.labTest.button'),
            clickAction: () => {
              this.gtmPush({
                event: 'button_click',
                'button.type': this.$t('dashboard.services.labTest.button')
              });
              this.updateFormDialogs({
                formName: 'interpretation-of-results',
                value: true
              });
            }
          }
        ]
      };
    },
    videoCallService() {
      return {
        icon: 'phone-video',
        title: this.$t('dashboard.services.videoCall.title'),
        subtitle: this.$t('dashboard.services.videoCall.subtitle'),
        text: this.$t('dashboard.services.videoCall.text'),
        dataCy: 'clientZoneDashboardServicesVideoCall',
        buttons: [
          {
            type: 'block',
            text: this.$t('dashboard.services.videoCall.button'),
            clickAction: () => {
              this.gtmPush({
                event: 'button_click',
                'button.type': this.$t('dashboard.services.videoCall.button')
              });
              this.updateFormDialogs({
                formName: 'videocall',
                value: true
              });
            }
          }
        ]
      };
    },
    orderService() {
      return {
        icon: `calendar/date-${new Date().getDate()}`,
        title: this.$t('dashboard.services.appointment.title'),
        subtitle: this.$t('dashboard.services.appointment.subtitle'),
        text:
          this.$auth.user.services.orderMessage && this.$auth.user.services.order === 'visible'
            ? this.hintText(this.$auth.user.services.orderMessage)
            : this.$t('dashboard.services.appointment.text'),
        dataCy: 'clientZoneDashboardServicesAppointments',
        buttons: [
          {
            type: 'block',
            disabled: this.$auth.user.services.order === 'visible',
            text: this.$t('dashboard.services.appointment.button'),
            clickAction: () => {
              this.gtmPush({
                event: 'button_click',
                'button.type': this.$t('dashboard.services.appointment.button')
              });
              this.$router.push(this.$localePath('appointments'));
            }
          }
        ]
      };
    },
    preventionService() {
      return {
        icon: 'shield',
        title: this.$t('dashboard.services.prevention.title'),
        subtitle: this.$t('dashboard.services.prevention.subtitle'),
        text: this.$t('dashboard.services.prevention.text'),
        dataCy: 'clientZoneDashboardServicesPrevention',
        buttons: [
          {
            type: 'block',
            disabled: this.$auth.user.services.prevention === 'visible',
            text: this.$t('dashboard.services.prevention.existedButton'),
            clickAction: () => {
              this.gtmPush({
                event: 'button_click',
                'button.type': this.$t('dashboard.services.prevention.existedButton')
              });
              this.$router.push(this.$localePath('prevention'));
            }
          }
        ]
      };
    },
    thematicCounsellingService() {
      return {
        title: this.$t('dashboard.services.thematic_counseling.title'),
        text: this.$t('dashboard.services.thematic_counseling.text'),
        dataCy: 'clientZoneDashboardServicesThematicCounseling',
        buttons: [
          {
            type: 'block',
            color: this.availableServiceCount > 2 ? 'tetrary' : undefined,
            isCardAction: this.availableServiceCount > 2,
            text: this.$t('dashboard.services.thematic_counseling.button'),
            link: import.meta.env.VITE_OLD_WEB_URL + '/tematicke-poradny'
          }
        ]
      };
    },
    covidLineService() {
      if (this.covidLineAvailable) {
        return {
          title: this.covidLine.title,
          text: this.covidLine.phone.time,
          buttons: [
            {
              type: 'block',
              color: 'tetrary',
              isCardAction: true,
              text: this.covidLine.phone.number,
              link: `tel:${this.linkerizePhone(this.covidLine.phone.number)}`
            }
          ],
          tooltip: {
            title: this.$t('hrbPage.covidLink.tooltip.title'),
            type: 'list',
            content: this.$t('hrbPage.covidLink.tooltip.text', { returnObjects: true })
          }
        };
      } else {
        return {};
      }
    },
    assistLineService() {
      const buttonsBase = [];

      if (this.assistLine.phone) {
        buttonsBase.push({
          text: this.assistLine.phone.number,
          link: `tel:${this.linkerizePhone(this.assistLine.phone.number)}`
        });
      }

      if (this.assistLine.email) {
        buttonsBase.push({
          text: this.assistLine.email,
          link: `mailto:${this.assistLine.email}`
        });
      }

      if (this.assistLine.url) {
        buttonsBase.push({
          text: this.assistLine.urlText || this.assistLine.url,
          link: this.assistLine.url,
          newTabIcon: !!this.assistLine.urlText
        });
      }

      return {
        title: this.assistLine.title,
        text: this.assistLine.description,
        buttons: buttonsBase.map(btn => {
          return {
            type: 'block',
            color: 'tetrary',
            isCardAction: true,
            ...btn
          };
        })
      };
    },
    videoTherapyService() {
      const isVideotherapyVisible = this.$auth.user.services.videotherapy === 'visible';

      return {
        icon: 'brain',
        title: this.$t('dashboard.services.videoTherapy.title'),
        subtitle: this.$t('dashboard.services.videoTherapy.subtitle'),
        text: isVideotherapyVisible
          ? this.$t('dashboard.services.videoTherapy.textUnavailable')
          : this.$t('dashboard.services.videoTherapy.text'),
        dataCy: 'mentalHealthCall',
        buttons: [
          {
            type: 'block',
            disabled: isVideotherapyVisible,
            text: this.$t('dashboard.services.videoTherapy.button'),
            clickAction: async () => {
              this.gtmPush({
                event: 'button_click',
                'button.type': this.$t('dashboard.services.videoTherapy.button')
              });
              await this.$store.dispatch('auth/fetchUser');
              if (isVideotherapyVisible) {
                return;
              }
              this.updateFormDialogs({
                formName: 'videotherapy',
                value: true
              });
            }
          }
        ]
      };
    }
  },
  methods: {
    hintText(message) {
      let text = '';
      switch (message) {
        case 'service_not_supported_in_this_language':
          text = this.$t(`dashboard.services.hints.${message}.text`, {
            language: this.$t(`dashboard.services.hints.${message}.${this.$auth.user.language}`),
            link: this.$localePath('accountSettings')
          });
          break;
      }

      return text;
    }
  }
};
